import _ from "lodash";

/** One choice already on a Uniformax size list, as placement needs to see it. */
export interface PlacedSize {
  id: number;
  value: string;
  order: number;
}

export interface SizePlacement {
  /** Display order for each new size, keyed by its value. */
  orders: Map<string, number>;
  /** Existing choices whose display order has to move. Empty unless a gap was too small. */
  reorders: { id: number; order: number }[];
}

/**
 * Decides when a feed size can be copied onto a Uniformax size list word for word, and where it
 * goes in the list.
 */
export class FeedSizeFns {
  /** Exact matches a shape needs before the list counts as writing sizes the feed's way. */
  static readonly MIN_PROOF = 3;

  /** '52-26' -> '#-#', '28U' -> '#U', '28 36U' -> '# #U'. Digits collapse; everything else stays. */
  static shape(size: string): string {
    return (size ?? "").trim().toUpperCase().replace(/\d+/g, "#");
  }

  /** '52-26' -> [52, 26]. */
  static numbers(size: string): number[] {
    return ((size ?? "").match(/\d+/g) ?? []).map(Number);
  }

  static compareNumbers(a: number[], b: number[]): number {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return a.length - b.length;
  }

  /**
   * Shapes the list provably writes the feed's way.
   *
   * Numeric shapes only. The numbers are what place a new size in the list, and a shape with no
   * digits matches only its own spelling - which, being an exact match, is already on the list.
   */
  static provenShapes(exactMatches: string[], minProof = FeedSizeFns.MIN_PROOF): Set<string> {
    const counts = _.countBy(exactMatches.map(s => FeedSizeFns.shape(s)));
    return new Set(Object.keys(counts).filter(s => s.includes("#") && counts[s] >= minProof));
  }

  /**
   * Where new sizes go on a list, by display order.
   *
   * Each goes directly before the first size of its own shape that is bigger, so 29-24 lands after
   * 28U and not among the 28s. With nothing bigger it goes directly after the biggest, so 54-35
   * lands before 54U. Sizes of another shape keep their place.
   *
   * New sizes fill the gaps in the existing numbering - the list is spaced by ten - so nothing that
   * is already there moves. Only when a gap is too small is the whole list renumbered by ten.
   */
  static placeNewSizes(existing: PlacedSize[], newValues: string[]): SizePlacement {
    type Slot = { value: string; isNew: boolean; id?: number; order?: number };
    const merged: Slot[] = _.sortBy(existing, [e => e.order ?? 0, e => e.value])
      .map(e => ({ value: e.value, isNew: false, id: e.id, order: e.order ?? 0 }));

    const ascending = [...newValues].sort((a, b) =>
      FeedSizeFns.compareNumbers(FeedSizeFns.numbers(a), FeedSizeFns.numbers(b)));

    for (const value of ascending) {
      const shape = FeedSizeFns.shape(value);
      const nums = FeedSizeFns.numbers(value);
      const sameShape = (s: Slot) => FeedSizeFns.shape(s.value) === shape;
      const cmp = (s: Slot) => FeedSizeFns.compareNumbers(FeedSizeFns.numbers(s.value), nums);

      const slot: Slot = { value, isNew: true };
      const successor = merged.findIndex(s => sameShape(s) && cmp(s) > 0);
      if (successor >= 0) {
        merged.splice(successor, 0, slot);
        continue;
      }
      const predecessor = _.findLastIndex(merged, s => sameShape(s) && cmp(s) < 0);
      if (predecessor >= 0) merged.splice(predecessor + 1, 0, slot);
      else merged.push(slot);
    }

    const orders = new Map<string, number>();
    let fits = true;
    let i = 0;
    while (i < merged.length && fits) {
      if (!merged[i].isNew) { i++; continue; }

      const start = i;
      while (i < merged.length && merged[i].isNew) i++;
      const run = merged.slice(start, i);
      const before = start > 0 ? merged[start - 1].order! : 0;
      const after = i < merged.length ? merged[i].order! : before + 10 * (run.length + 1);

      const step = Math.floor((after - before) / (run.length + 1));
      if (step < 1) { fits = false; break; }
      run.forEach((s, k) => orders.set(s.value, before + step * (k + 1)));
    }
    if (fits) return { orders, reorders: [] };

    orders.clear();
    const reorders: { id: number; order: number }[] = [];
    merged.forEach((s, k) => {
      const order = (k + 1) * 10;
      if (s.isNew) orders.set(s.value, order);
      else if (s.order !== order) reorders.push({ id: s.id!, order });
    });
    return { orders, reorders };
  }
}
